import { isFunction, get, isArray, isObject, isString, isNumber, forEach, has } from 'lodash';

import {
  CALCULATE_VALUE_RULES,
  defaultPhases,
  INVALID_DATA_FORMAT_ERROR_MESSAGE,
  INVALID_RULES_FORMAT_ERROR_MESSAGE,
  PGEPeriodLabelMap,
} from '../../constants';

export const disableChargerField = (params) =>
  get(params, 'node.staticSection.condition') === 'INACTIVE' || params.parent === null;

export const getValue = (params, path) => {
  const param = get(params, path);

  return isFunction(param) ? param(params) : param;
};

export const getSourceValue = (params) => {
  const {
    node,
    field: { source },
  } = params;

  return isFunction(source) ? source(params) : get(node, source);
};

export const getValueFromSource = (params, source) => get(params, source);

export const getCalculatedSourceValue = (params) => {
  const { node } = params;

  if (!node) {
    return;
  }

  const getCalculationRule = CALCULATE_VALUE_RULES[node.topologyNodeType];

  if (!getCalculationRule) {
    return;
  }

  return getCalculationRule(params);
};

export const getMultiplySourceValue = (params) => {
  const {
    node,
    field: { source },
  } = params;
  const multiply = getValue(params, 'field.multiply');
  const accuracy = getValue(params, 'field.accuracy');

  if (multiply && isNumber(multiply)) {
    const value = get(node, source) * multiply;

    return isFunction(source) ? source(params) : value.toFixed(accuracy || 2);
  }

  return isFunction(source) ? source(params) : get(node, source);
};

export const getStateValue = (params) => {
  const {
    formState,
    field: { source },
  } = params;

  return isFunction(source) ? source(params) : get(formState, source);
};

export const makePGESelectOptions = () =>
  Object.keys(PGEPeriodLabelMap).map((key) => ({ value: key, label: PGEPeriodLabelMap[key] }));

export const defaultPropertyExtractor = (propName) => ({ [propName]: (props) => props[propName] });

export const parsePhaseString = (str) => {
  const parsed = {};
  const splitted = str.split('');

  for (let i = 0; i < splitted.length; i++) {
    parsed[i + 1] = splitted[i];
  }

  return parsed;
};

export const parsePhases = (str) => {
  if (!isString(str)) return str;

  return {
    relativeRotation: parsePhaseString(str),
  };
};

export const phaseKey = (phase) =>
  Object.keys(phase)
    .reduce((acc, key) => {
      acc.push(phase[key]);

      return acc;
    }, [])
    .join('');

export const getPhasesSource = (params) => {
  const relativeRotation = get(params, 'node.staticSection.phases.relativeRotation');

  return phaseKey(relativeRotation || defaultPhases.relativeRotation);
};

export const getPhaseConnectivitySource = (params) => {
  const phases = get(params, 'node.staticSection.phases');

  if (isString(phases)) {
    return phases.length;
  }

  if (isObject(phases)) {
    return Object.keys(phases.relativeRotation).length;
  }

  return 3;
};

const getParent = (childId, nodes) => nodes.find((node) => node.children.find((child) => child.id === childId));

export const getAvailablePhases = (node, nodes = []) => {
  const phases = get(node, 'staticSection.phases');

  if (node && !phases) {
    const parent = getParent(node.id, nodes);

    if (parent) {
      return getAvailablePhases(parent, nodes);
    }
  }

  const parentRotation = get(node, 'staticSection.phases.relativeRotation');

  return parentRotation ? Object.keys(parentRotation).length : null;
};

export const getPhaseConnectivityOptions = (parentNode, nodes) => {
  const options = [];

  for (let i = 0; i < (getAvailablePhases(parentNode, nodes) || 3); i++) {
    options.push({ value: i + 1, label: `${i + 1}-Phases` });
  }

  return options;
};

export const getPhasesOptions = (props) => {
  const { formState, parent, nodes } = props;

  const phaseConnectivity =
    get(formState, 'staticSection.phaseConnectivityType') || getPhaseConnectivitySource(props) || 3;

  const parentPhases = getAvailablePhases(parent, nodes) || 3;

  switch (parentPhases * 10 + phaseConnectivity) {
    case 11:
      return [{ value: '1', label: '1' }];
    case 21:
      return [
        { value: '1', label: '1' },
        { value: '2', label: '2' },
      ];
    case 22:
      return [
        { value: '12', label: '12' },
        // { value: '21', label: '21' }
      ];
    case 31:
      return [
        { value: '1', label: '1' },
        { value: '2', label: '2' },
        { value: '3', label: '3' },
      ];
    case 32:
      return [
        { value: '12', label: '12' },
        // { value: '5', label: '21' },
        { value: '13', label: '13' },
        // { value: '7', label: '31' },
        { value: '23', label: '23' },
        // { value: '9', label: '32' },
      ];
    case 33:
    default:
      return [
        { value: '123', label: '123' },
        { value: '231', label: '231' },
        { value: '312', label: '312' },
      ];
  }
};

const invalidRulesFormatException = (rules) => {
  if (!rules || !isArray(rules.tabs)) {
    throw Error(INVALID_RULES_FORMAT_ERROR_MESSAGE);
  }
};

const invalidDataFormatException = (data) => {
  if (!data || !isObject(data.staticSection)) {
    throw Error(INVALID_DATA_FORMAT_ERROR_MESSAGE);
  }
};

/**
 * Extract and map fields by name, from the form rules
 * Valid rules format: { tabs: [] }
 *
 * @param rules
 * @returns {{}}
 */
export const makeFieldListByName = (rules) => {
  invalidRulesFormatException(rules);

  const fieldsByName = {};

  rules.tabs.forEach((tab) => {
    tab.fields.forEach((field) => {
      if (field && field.name) {
        fieldsByName[field.name] = field;
      }
    });
  });

  return fieldsByName;
};

/**
 * Validate form data according field and global validators
 * Valid data format: { staticSection: {} }
 *
 * @param data
 * @param rules
 * @returns {{isValid: boolean, errors: {global: *[], fields: {}}}}
 */

export const validateFormData = ({ data, rules }) => {
  invalidDataFormatException(data);

  let isValid = true;
  const fieldErrors = {};
  const fieldsByName = makeFieldListByName(rules);

  Object.keys(data.staticSection).forEach((dataKey) => {
    const validators = fieldsByName[dataKey] ? getValue(fieldsByName[dataKey], 'validators') || [] : [];

    validators.forEach((validator) => {
      const validateResult = validator(data.staticSection[dataKey], { data, dataKey, validators });

      if (validateResult) {
        if (!fieldErrors[dataKey]) fieldErrors[dataKey] = [];
        if (isFunction(validateResult)) {
          if (validateResult(data)) {
            fieldErrors[dataKey].push(validateResult(data));
            isValid = false;
          }
        } else {
          fieldErrors[dataKey].push(validateResult);
          isValid = false;
        }
      }
    });
  });
  // separate logic for schedules field
  if (has(data, 'staticSection.schedules')) {
    get(data, 'staticSection.schedules', []).forEach((dayTypeObj) => {
      const validators = get(fieldsByName, `${dayTypeObj.dayType}.validators`, []);

      validators.forEach((validator) => {
        const items = get(dayTypeObj, 'items', []);
        const validateResult = validator(items);

        if (validateResult) {
          if (!fieldErrors[`schedules#${dayTypeObj.dayType}`]) fieldErrors[`schedules#${dayTypeObj.dayType}`] = [];
          fieldErrors[`schedules#${dayTypeObj.dayType}`].push(validateResult);
          isValid = false;
        }
      });
    });
  }

  return {
    isValid,
    errors: {
      fields: fieldErrors,
      global: [],
    },
  };
};

export const handleFieldChanges = (event) => {
  const { name, value, type, checked } = event.target;
  let actualValue = value;

  if (type === 'checkbox') {
    actualValue = checked;
  }

  if (type === 'timeFrom') {
    actualValue = parseInt(value.format('H'), 10) * 60 * 60 + parseInt(value.format('m'), 10) * 60;
  }

  if (type === 'timeTo') {
    actualValue = parseInt(value.format('H'), 10) * 60 * 60 + parseInt(value.format('m'), 10) * 60;

    if (actualValue === 0) actualValue = 86399;
  }

  return {
    event,
    name,
    value: actualValue,
    checked,
  };
};

const hasGapsOrOverlaps = (ranges) => {
  if (ranges.length <= 1) {
    return false;
  }

  let prevRange = ranges[0];

  for (let i = 1; i < ranges.length; i++) {
    if (ranges[i] && prevRange && ranges[i].from - prevRange.to === 1) {
      prevRange = ranges[i];
    } else {
      return true;
    }
  }

  return false;
};

const isCoverFullDay = (ranges) => {
  const filteredRanges = ranges.filter((range) => range !== null);
  const sum = filteredRanges.reduce((sum, { from, to }) => sum + (to - from), 0);

  return sum + filteredRanges.length === 86400;
};

const checkFromTo = (ranges) =>
  ranges.reduce((errors, range, index) => {
    if (range && range.to - range.from <= 0) {
      errors.push({
        index,
        field: 'from',
        message: 'Must be less than To.',
      });

      errors.push({
        index,
        field: 'to',
        message: 'Must be greater than From.',
      });
    }

    return errors;
  }, []);

const checkNegative = (ranges) =>
  ranges.reduce((errors, range, index) => {
    if (range) {
      forEach(range, (value, field) => {
        if (isNumber(value) && value < 0) {
          errors.push({
            index,
            field,
            message: 'Must be non negative number.',
          });
        }
      });
    }

    return errors;
  }, []);

export const validateRanges = (ranges) => [...checkFromTo(ranges), ...checkNegative(ranges)];

export const validateSchedule = (ranges) => {
  if (hasGapsOrOverlaps(ranges) || !isCoverFullDay(ranges)) {
    return {
      message: 'Schedule must cover full day and have no gaps or overlaps.',
    };
  }

  return null;
};
